import { useEffect, useRef } from "react";

export const useGameStateListeners = (
  socket,
  name,
  roundNumber,
  {
    setPlayers,
    setPlayerColors,
    setPlayerEmojis,
    setPlayerActionCards,
    setMaxRounds,
    setMaxActionsPerTurn,
    setMaxActionCards,
    setCurrentPhase,
    setGameLog,
    setCurrentTurn,
    setMyTurn,
    setPlayerCash,
    setUpdates,
    setHasJoinedGame,
    hasJoinedGameRef,
    showMessage,
    addNotification,
    clearAllNotifications,
    notificationRefs,
    dismissingRef,
    initializeFromGameLog,
    firstTurnRef
  }
) => {
  useEffect(() => {
    const currentSocket = socket.current;
    if (!currentSocket) return;

    const handleStartVariables = ({ playerNames, playerColors, playerEmojis, actionCards, gameLog, maxActionsPerTurn, maxActionCards, maxRounds, currentPhase }) => {
      if (process.env.NODE_ENV === 'development') {
        console.log('[Socket Broadcast] start_variables received');
      }
      clearAllNotifications();
      if (notificationRefs) notificationRefs.current = {};
      if (dismissingRef) dismissingRef.current.clear();
      
      firstTurnRef.current = true;
      
      setHasJoinedGame(true);
      hasJoinedGameRef.current = true;
      setPlayers(playerNames || []);
      setPlayerColors(playerColors || {});
      setPlayerEmojis(playerEmojis || {});
      setPlayerActionCards(Array.isArray(actionCards) ? actionCards : []);
      if (maxRounds !== undefined && maxRounds !== null) {
        setMaxRounds(maxRounds);
      }
      if (maxActionsPerTurn !== undefined && maxActionsPerTurn !== null) {
        setMaxActionsPerTurn(maxActionsPerTurn);
      }
      if (maxActionCards !== undefined && maxActionCards !== null) {
        setMaxActionCards(maxActionCards);
      }
      setCurrentPhase(currentPhase || null);
      setGameLog([]);
      if (Array.isArray(gameLog) && gameLog.length > 0) {
        setGameLog(gameLog);
        initializeFromGameLog(gameLog);
      }
    };

    const handleCashUpdate = (cash) => {
      if (process.env.NODE_ENV === 'development') {
        console.log('[Socket Broadcast] cash_update received');
      }
      setPlayerCash(cash || {});
    };

    const handlePlayerColors = (colors) => {
      if (process.env.NODE_ENV === 'development') {
        console.log('[Socket Broadcast] player_colors received');
      }
      setPlayerColors(colors || {});
    };

    const handlePlayerEmojis = (emojis) => {
      if (process.env.NODE_ENV === 'development') {
        console.log('[Socket Broadcast] player_emojis received');
      }
      setPlayerEmojis(emojis || {});
    };

    const handleUpdate = ({ name, send, length }) => {
      if (process.env.NODE_ENV === 'development') {
        console.log('[Socket Broadcast] update received', { name });
      }
      const sendStr =
        typeof send === "string"
          ? send
          : send && send.value && send.suit
          ? `${send.value} of ${send.suit}`
          : JSON.stringify(send);
      setUpdates((updates) => [...updates, `${name} threw ${sendStr}(${length})`]);
    };

    const handlePhaseUpdate = (phase) => {
      if (process.env.NODE_ENV === 'development') {
        console.log('[Socket Broadcast] phase_update received', { phase });
      }
      setCurrentPhase(phase);
    };

    const handleTurnOrderUpdate = (playerNames) => {
      if (process.env.NODE_ENV === 'development') {
        console.log('[Socket Broadcast] turn_order_update received');
      }
      if (Array.isArray(playerNames)) {
        setPlayers(playerNames);
      }
    };

    const handleYourTurn = (player_name) => {
      if (process.env.NODE_ENV === 'development') {
        console.log('[Socket Broadcast] your_turn received', { player_name });
      }
      setCurrentTurn(player_name);
      if (player_name === name) {
        const isFirstTurnFirstRound = firstTurnRef.current && roundNumber === 1;
        let message = `It's your turn, ${player_name}!`;
        if (isFirstTurnFirstRound) {
          message += `\n\n💡 Need help? Check out the 📖 Game Manual and ℹ️ Interactive Tutorial buttons in the top right!`;
          firstTurnRef.current = false;
        }
        showMessage(message);
        setMyTurn(true);
      } else {
        addNotification(`🎯 ${player_name}'s turn`, player_name);
      }
    };

    const handleRoundStartMessage = ({ round, startingPlayer, message }) => {
      if (process.env.NODE_ENV === 'development') {
        console.log('[Socket Broadcast] round_start_message received', { round });
      }
      showMessage(message);
    };

    const handlePlayerQuitNotification = (message) => {
      if (process.env.NODE_ENV === 'development') {
        console.log('[Socket Broadcast] player_quit_notification received');
      }
      showMessage(message);
    };

    const handleMaxRoundsUpdate = (maxRounds) => {
      if (process.env.NODE_ENV === 'development') {
        console.log('[Socket Broadcast] max_rounds_update received', { maxRounds });
      }
      if (maxRounds !== undefined && maxRounds !== null) {
        setMaxRounds(maxRounds);
      }
    };

    currentSocket.on("start_variables", handleStartVariables);
    currentSocket.on("cash_update", handleCashUpdate);
    currentSocket.on("player_colors", handlePlayerColors);
    currentSocket.on("player_emojis", handlePlayerEmojis);
    currentSocket.on("update", handleUpdate);
    currentSocket.on("phase_update", handlePhaseUpdate);
    currentSocket.on("turn_order_update", handleTurnOrderUpdate);
    currentSocket.on("your_turn", handleYourTurn);
    currentSocket.on("round_start_message", handleRoundStartMessage);
    currentSocket.on("player_quit_notification", handlePlayerQuitNotification);
    currentSocket.on("max_rounds_update", handleMaxRoundsUpdate);

    return () => {
      currentSocket.off("start_variables", handleStartVariables);
      currentSocket.off("cash_update", handleCashUpdate);
      currentSocket.off("player_colors", handlePlayerColors);
      currentSocket.off("player_emojis", handlePlayerEmojis);
      currentSocket.off("update", handleUpdate);
      currentSocket.off("phase_update", handlePhaseUpdate);
      currentSocket.off("turn_order_update", handleTurnOrderUpdate);
      currentSocket.off("your_turn", handleYourTurn);
      currentSocket.off("round_start_message", handleRoundStartMessage);
      currentSocket.off("player_quit_notification", handlePlayerQuitNotification);
      currentSocket.off("max_rounds_update", handleMaxRoundsUpdate);
    };
  }, [socket, name, roundNumber, setPlayers, setPlayerColors, setPlayerEmojis, setPlayerActionCards, setMaxRounds, setMaxActionsPerTurn, setMaxActionCards, setCurrentPhase, setGameLog, setCurrentTurn, setMyTurn, setPlayerCash, setUpdates, setHasJoinedGame, hasJoinedGameRef, showMessage, addNotification, clearAllNotifications, notificationRefs, dismissingRef, initializeFromGameLog, firstTurnRef]);
};

